<template>
  <div class="w-full flex flex-col flex-grow p-1 items-center">
<!-- Inputs -->
    <div class="w-full flex flex-row py-1 gap-1 border-b justify-center flex-wrap-reverse">
<!-- Text input + submit -->
      <div class="w-full flex flex-col max-w-md">
        <div class="w-full flex flex-row items-center space-x-1 max-w-md px-1 py-0.5 flex-nowrap">
          <input
            ref="searchInputRef"
            v-model="q"
            type="search"
            class="w-full"
            :class="inputBorderClass"
            placeholder="Search by serial, MAC, tracking, part #, manufacturer, or keyword"
            maxlength="128"
            autocomplete="off"
            :disabled="isExporting"
            @paste="onPaste"
            @keydown.enter="onSubmit"
          />
          <button v-if="q" class="bn-icon-only" @click="clearAll" :disabled="isWorking || isExporting">
            <XIcon />
          </button>
        </div>
        <div class="w-full max-w-md flex items-center flex-wrap px-2">
          <div class="flex-grow items-center flex flex-nowrap mr-1 py-0.5">
            <div class="w-auto mr-2 text-left whitespace-normal break-words text-indigo py-0.5">Received:</div>
            <button class="bn-text" :class="lookbackMonths ? '' : 'text-amber'" @click="toggleLookback" :disabled="isWorking || isExporting">
              {{ lookbackMonths ? `Last ${DEFAULT_LOOKBACK_MONTHS} months` : 'All time' }}
            </button>
          </div>
          <div class="w-auto py-0.5">
            <button
              class="bn-text flex flex-row items-center"
              :class="exactMatch ? 'text-green' : 'opacity-40'"
              @click="toggleExactMatch"
              :disabled="isWorking || isExporting"
            >
              <CheckIcon v-if="exactMatch" class="w-4 h-4 mr-1" />
              <div>Exact Match</div>
            </button>
          </div>
        </div>
      </div>
<!-- Customer chip — primary users only -->
      <div v-if="isPrime && !projectId" class="w-full max-w-md px-1 py-0.5">
        <div v-if="filters.client?._id" class="w-full flex flex-row items-center space-x-1 px-2 py-0.5 rounded-md">
          <div class="w-auto truncate text-left whitespace-normal break-words font-semibold text-sky mr-3">{{ filters.client?.displayName }}</div>
          <button class="bn-icon-only" @click="clearClient" :disabled="isWorking || isExporting"><XIcon /></button>
        </div>
        <searchApi v-else apiEndpoint="search/organizations" :displayResult="false" placeholder="Narrow By Customer" @resultSelected="selectClient($event)" :disabled="isWorking || isExporting" />
      </div>
    </div>
<!-- Loading and Errors -->
    <WorkingAndError :isWorking="isWorking" :error="responseError" @clearError="responseError = null" />
<!-- Results status bar: count + local filters. v-show (not v-if) so the selects stay mounted
       across fetches and retain their bound state/shipment/query values across the result clear. -->
    <div v-show="results.length > 0" class="w-full max-w-7xl flex flex-row flex-wrap items-center px-1">
      <div class="w-auto min-w-44 truncate text-left">{{ countLabel }}</div>
      <div class="w-auto flex flex-row flex-wrap items-center gap-1">
        <div class="p-1">
          <button v-if="canLoadMore" class="bn-text mr-1 text-[11px]" @click="loadMore" :disabled="isWorking || isExporting">Load More..</button>
        </div>
        <div class="p-1">
          <button v-if="canLoadAll" class="bn-text text-amber text-[11px]" @click="loadAll" :disabled="isWorking || isExporting">Load All</button>
        </div>
        <div v-if="showNarrowHint" class="flex-grow text-left whitespace-normal p-1">
          Narrow your search to see more matches.
        </div>
      </div>
      <div class="flex-grow flex-row items-center p-1 justify-center min-w-60 px-2">
        <input type="search" v-model="localFilters.query" class="w-full min-w-24 md:max-w-72" :class="{ 'border-indigo-500/30 dark:border-sky-500/30': localFilters.query }" placeholder="filter results" :disabled="isExporting">
      </div>
      <div class="flex flex-row flex-nowrap items-center gap-1 pl-3 md:pl-1">
        <template v-if="!hideStateShipment">
          <span>Narrow:</span>
          <select v-model="localFilters.state" class="w-32 mr-1" :class="{ 'border-indigo-500/30 dark:border-sky-500/30': localFilters.state }" :disabled="isExporting">
            <option value="">State: any</option>
            <option value="assigned">Assigned</option>
            <option value="unassigned">Unassigned</option>
            <option value="racked">Racked</option>
          </select>
          <select v-model="localFilters.shipment" class="w-32" :class="{ 'border-indigo-500/30 dark:border-sky-500/30': localFilters.shipment }" :disabled="isExporting">
            <option value="">Shipment: any</option>
            <option value="shipped">Shipped</option>
            <option value="unshipped">No Shipment</option>
          </select>
        </template>
        <div class="p-1">
          <button class="bn-icon-only" @click="onExportExcel" :disabled="isWorking || isExporting || displayResults.length === 0">
            <ExcelIcon />
          </button>
        </div>
      </div>
    </div>
    <div class="w-full max-w-7xl py-0.5 px-2">
      <div v-if="hasActiveLocalFilters && results.length" class="w-full text-left whitespace-normal">
        <span class="text-indigo dark:text-amber mr-0.5">{{ displayResults.length }}</span> filtered results of {{ results.length }}
      </div>
    </div>
<!-- Results -->
    <div class="w-full flex flex-col flex-grow flex-auto h-0 overflow-y-auto p-1 items-center">
      <div class="w-full max-w-7xl flex flex-col space-y-1">
<!-- Empty / hint states -->
        <div v-if="!isWorking && displayResults.length === 0 && hasRun" class="w-full p-4 text-center">
          No equipment found.
        </div>
<!-- Result rows -->
        <EquipmentSearchRow
          v-for="row in displayResults"
          :key="row._id"
          :equipment="row"
          :isPrime="isPrime"
          :hide-state-shipment="hideStateShipment"
          :menu-open="openMenuId === row._id"
          :on-toggle-menu="toggleRowMenu"
          :today-tick="todayTick"
          :can-delete="canDelete"
          @open="openDetail(row._id)"
          @openPackingList="onOpenPackingList"
          @delete="deleteBox"
        />
      </div>
    </div>
<!-- Detail modal -->
    <Modal v-if="detailId" :title="isPrime ? 'Edit Box' : 'View Box'" @closeModal="detailId = null">
      <template v-slot:content>
        <EditBox :equipmentId="detailId" :allowEdit="isPrime" :external="false" :project="null" @updateEquipment="onDetailUpdated" />
      </template>
    </Modal>
<!-- Packing list modal (mongo-doc path) -->
    <Modal v-if="packingListDetailId" title="Packing List" @closeModal="packingListDetailId = null">
      <template v-slot:content>
        <PackingListDetailPanel :packing-list-id="packingListDetailId" :draft-packing-list="null" />
      </template>
    </Modal>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { subMonths, startOfDay, format, parseISO, isValid } from 'date-fns'
import api from '@/api.js'
import global from '@/global'
import { capitalizeString, camelCaseToLabel, formatBoxNumber, broadcastChange } from '@/shared'
import ExcelJs from 'exceljs'
import { XIcon, CheckIcon } from '@heroicons/vue/outline'
import ExcelIcon from '@/components/customIcons/ExcelIcon.vue'
import searchApi from '@/components/ui/searchApi.vue'
import EquipmentSearchRow from './EquipmentSearchRow.vue'
import EditBox from '@/components/projects/equipment/menuOptions/EditBox.vue'
import PackingListDetailPanel from '@/components/warehouse/receiving/PackingListDetailPanel.vue'

const DEBOUNCE_MS = 300
const MIN_AUTO_QUERY_CHARS = 3
const PAGE_SIZE = 200
const MAX_SKIP = 800         // matches backend clamp; offer "Load more" until we've shown MAX_SKIP + PAGE_SIZE
// Lookback toggle: 12 months (default) ↔ 0 (All time). 0 sends createdAfter=null to the backend
// which interprets it as no date filter. Missing on a direct API call falls back to DEFAULT_LOOKBACK_MONTHS.
const DEFAULT_LOOKBACK_MONTHS = 12

export default {
  components: { XIcon, searchApi, EquipmentSearchRow, EditBox, PackingListDetailPanel,ExcelIcon,CheckIcon },
  props: {
    // Optional scope: restrict results to a single project (e.g. a fulfillment pool). Null = the normal
    // access-scoped app-wide search. Reused by the fulfillment equipment modal.
    projectId: { type: String, default: null },
    // Hide the State / Shipment narrow filters + the per-row state/shipment chips. Pool inventory is
    // always unassigned/unshipped, so those are noise in the fulfillment context.
    hideStateShipment: { type: Boolean, default: false }
  },
  setup(props) {
    const { authenticated } = global

  // Refs
    const q = ref('')
    const results = ref([])
    const filters = ref({ client: {} })
    const localFilters = ref({ state: '', shipment: '', query: '' })
    const resultMeta = ref({ count: 0, approximate: false, hasMore: false, cappedAtLimit: false })
    const isWorking = ref(false)
    const isExporting = ref(false)
    const responseError = ref(null)
    const hasRun = ref(false)
    const detailId = ref(null)
    const packingListDetailId = ref(null)
    const searchInputRef = ref(null)
    const openMenuId = ref(null)
    // Day tick: increments at local midnight so child rows' time-sensitive computeds
    // (Shipping vs Shipped, past/future shipment color) re-evaluate without per-row timers.
    const todayTick = ref(0)
    // Lookback floor on createdAt — every search request includes the resulting cutoff.
    const lookbackMonths = ref(DEFAULT_LOOKBACK_MONTHS)
    // Identifier-only search mode. ON → backend prunes the criteria-field branches and the
    // multi-token phrase boost; only trackingNumber / partNumber / serialNumber / macAddress
    // (+ embedded arrays + MAC `:`-token regex) score. Off by default; in-session persistence.
    const exactMatch = ref(false)

    let activeController = null
    let debounceTimer = null

  // Computed
    const isPrime = computed(() => !!authenticated.value?.organization?.orgType?.includes?.('primary'))
    // Stricter than isPrime on purpose: the delete route is primeOnly() + accessControl({warehouseQueue:2})
    // with no projects fallback, so anyone below that tier would only get a 403 from a menu item they saw.
    const canDelete = computed(() => isPrime.value && authenticated.value?.role?.accessModel?.warehouseQueue >= 2)

    const canQuery = computed(() => q.value.trim().length >= MIN_AUTO_QUERY_CHARS || !!filters.value.client?._id)

    // Cutoff = start-of-local-day of N months ago. Stable across re-runs within the same day,
    // and immune to time-of-day drift. ISO sent to backend; parsed there with Date.parse.
    // null when "All time" is selected (lookbackMonths === 0); backend treats null as no filter.
    const createdAfterISO = computed(() => {
      if (!lookbackMonths.value) return null
      return startOfDay(subMonths(new Date(), lookbackMonths.value)).toISOString()
    })

    // Subtle validity hint on the search input: red while below the auto-query threshold, green once valid, neutral when empty.
    const inputBorderClass = computed(() => {
      const len = q.value.trim().length
      if (len === 0) return 'border-sky-500/40'
      if (len < MIN_AUTO_QUERY_CHARS) return 'border-red-500/40'
      return 'border-green-500/40'
    })

    const countLabel = computed(() => {
      if (!results.value.length) return ''
      const shown = results.value.length
      // When approximate, the stored count equals the backend's COUNT_THRESHOLD (the lowerBound hit).
      const total = resultMeta.value.approximate ? `${resultMeta.value.count}+` : resultMeta.value.count
      return `Showing ${shown} of ${total} matches`
    })

    const canLoadMore = computed(() => resultMeta.value.hasMore && !resultMeta.value.cappedAtLimit && results.value.length < MAX_SKIP + PAGE_SIZE)
    const canLoadAll  = computed(() => resultMeta.value.hasMore && !resultMeta.value.cappedAtLimit)
    const showNarrowHint = computed(() => resultMeta.value.cappedAtLimit || (resultMeta.value.hasMore && results.value.length >= MAX_SKIP + PAGE_SIZE))

    const hasActiveLocalFilters = computed(() => {
      const lf = localFilters.value
      return !!(lf.state || lf.shipment || (lf.query && lf.query.trim()))
    })

    // Client-side state/shipment refinement. State is derived from ref presence (roomId / rackId);
    // shipmentId is the rack-canonical overlay applied by the backend.
    // returnDisposition rules:
    //   - "unassigned" excludes disposition rows — those aren't operationally available even when
    //     their refs are empty.
    //   - "assigned" / "racked" INCLUDE disposition rows when the refs qualify, so data outliers
    //     (e.g. disposition set but still in a room) surface and the Row's disposition chip flags them.
    //   - "unshipped" excludes disposition rows — same reason as unassigned: a returned/disposed
    //     unit is not an operationally unshipped box waiting to ship.
    //   - "shipped" includes disposition rows when a shipmentId is present, since the row really
    //     did ship before the disposition was recorded.
    // multiBox rows are excluded from state/shipment filters: the backend's $first-sibling collapse
    // means the ref fields on the collapsed doc don't represent the whole unit, so filtering on them
    // would misrepresent the result set. Users open the row for the per-box breakdown.
    const displayResults = computed(() => {
      let rows = results.value
      const state = localFilters.value.state
      const shipment = localFilters.value.shipment
      if (state) {
        rows = rows.filter(r => {
          if (r.multiBox) return false
          const inRoom = !!r.roomId?._id
          const inRack = !!r.rackId?._id
          if (state === 'unassigned') return !inRoom && !inRack && !r.returnDisposition
          if (state === 'assigned')   return inRoom || inRack
          if (state === 'racked')     return inRack
          return true
        })
      }
      if (shipment) {
        rows = rows.filter(r => {
          if (r.multiBox) return false
          const shipped = !!r.shipmentId?._id
          if (shipment === 'shipped') return shipped
          return !shipped && !r.returnDisposition
        })
      }
      const needle = localFilters.value.query
      if (needle && needle.trim()) rows = rows.filter(r => matchesLocalQuery(r, needle))
      return rows
    })

  // API
    const cancelInFlight = () => {
      if (activeController) {
        try { activeController.abort() } catch (e) { /* ignore */ }
        activeController = null
      }
    }

    const runSearch = async ({ append = false, all = false } = {}) => {
      if (!canQuery.value) {
        results.value = []
        hasRun.value = false
        resultMeta.value = { count: 0, approximate: false, hasMore: false, cappedAtLimit: false }
        return
      }
      cancelInFlight()
      activeController = new AbortController()
      isWorking.value = true
      responseError.value = null
      // loadAll always replaces; append extends; otherwise fresh fetch.
      if (!append) results.value = []
      try {
        const body = {
          q: q.value.trim(),
          clientId: filters.value.client?._id || undefined,
          projectId: props.projectId || undefined,
          createdAfter: createdAfterISO.value,
          mode: exactMatch.value ? 'identifier' : undefined
        }
        if (all) {
          body.loadAll = true
        } else {
          body.limit = PAGE_SIZE
          body.skip = append ? results.value.length : 0
        }
        const res = await api.post('equipment/search', body, { signal: activeController.signal })
        const rows = res.data?.data || []
        results.value = append ? results.value.concat(rows) : rows
        resultMeta.value = {
          count: res.data?.count?.value ?? results.value.length,
          approximate: !!res.data?.count?.approximate,
          hasMore: !!res.data?.hasMore,
          cappedAtLimit: !!res.data?.cappedAtLimit
        }
        hasRun.value = true
      } catch (err) {
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return
        responseError.value = err.response?.data?.error || err.message
      } finally {
        isWorking.value = false
      }
    }

    const loadMore = () => runSearch({ append: true })
    const loadAll  = () => runSearch({ all: true })

    // Silent single-row refresh driven by a socket equipment-change event. Fetches the single doc
    // in search-row shape from /equipment/refresh-search-row (scoped get-by-id, not Atlas Search),
    // then re-locates by _id and replaces in place.
    // Null response (not found / out-of-scope) → silent remove. Network errors swallowed.
    const fetchAndReplaceOne = async (id) => {
      try {
        // noRetry: silent background refresh — don't let the global interceptor spin up retry loops.
        const res = await api.post('equipment/refresh-search-row', { _id: id }, { noRetry: true })
        const idx = results.value.findIndex(r => r._id === id)
        if (idx === -1) return // row disappeared between event and response — drop it
        const fresh = res.data?.data
        if (fresh) {
          // preserve _multiBoxQty from the old row (single-doc route doesn't recompute it)
          if (results.value[idx]._multiBoxQty != null && fresh._multiBoxQty == null) {
            fresh._multiBoxQty = results.value[idx]._multiBoxQty
          }
          results.value.splice(idx, 1, fresh)
        } else {
          results.value.splice(idx, 1)
        }
      } catch (err) { /* silent per socket-refetch convention */ }
    }

    // Delete an accidental scan. No ?amount: a search row is collapsed by box identity, so it IS the box —
    // the backend takes every piece or refuses. A kit container comes back with all of its members, so the
    // response ids (not the requested one) drive the local removal.
    // The deleting tab never hears its own socket broadcast, so the list is corrected from the response.
    const deleteBox = async (row) => {
      if (!row?._id) return
      isWorking.value = true
      responseError.value = null
      try {
        const res = await api.delete(`equipment/${row._id}`, { noRetry: true })
        const deleted = res.data?.data
        const deletedIds = Array.isArray(deleted) ? deleted.map(d => d._id) : (deleted?._id ? [deleted._id] : [row._id])
        const removeSet = new Set(deletedIds)
        const before = results.value.length
        results.value = results.value.filter(r => !removeSet.has(r._id))
        // Decrement by rows actually removed, not deletedIds.length — multiBox siblings were never
        // separate rows, and a kit's members may or may not have matched this search.
        const removed = before - results.value.length
        if (removed > 0) resultMeta.value.count = Math.max(0, resultMeta.value.count - removed)
        // Anything mounted behind this modal reacts exactly as it would to another user's change.
        broadcastChange('equipment', { equipmentIds: deletedIds, projectId: row.projectId?._id, isDelete: true })
      } catch (err) {
        responseError.value = err.response?.data?.error || err.message
      } finally {
        isWorking.value = false
      }
    }

  // Helpers
    // Word-start match across identifier / label fields; plain substring on description.
    // Word-start fields share the backend's non-alphanumeric tokenizer so "dm-nvx" and "aa:bb"
    // split naturally. Description is free-form prose where matching on the start of every
    // word is too strict — substring includes makes "rma" miss "Harman" (it's not in the
    // word-start set) but still find it inside a description sentence if that's relevant.
    // Spaces in the needle = AND across tokens; each needle token must satisfy EITHER:
    //   some word in the word-start fields starts with the token, OR
    //   the description contains the token as a substring.
    // Safe against missing refs and null subdoc values via optional chaining + type guards.
    const tokenize = (s) => (typeof s === 'string' ? s.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean) : [])
    const matchesLocalQuery = (r, needleRaw) => {
      const needleTokens = tokenize(needleRaw)
      if (!needleTokens.length) return true
      const wordStartValues = [
        r.trackingNumber, r.purchaseOrder, r.partNumber, r.serialNumber, r.macAddress,
        r.manufacturer, r.deviceType, r.returnDisposition,
        r.roomId?.nameOne, r.roomId?.nameTwo, r.roomId?.roomType, r.roomId?.estimateNumber, r.rackId?.name,
        r.boxNumber != null ? String(r.boxNumber) : ''
      ]
      if (Array.isArray(r.serialNumbers)) {
        for (const s of r.serialNumbers) if (s?.serialNumber) wordStartValues.push(s.serialNumber)
      }
      if (Array.isArray(r.macAddresses)) {
        for (const m of r.macAddresses) if (m?.macAddress) wordStartValues.push(m.macAddress)
      }
      const words = []
      for (const v of wordStartValues) for (const w of tokenize(v)) words.push(w)
      const description = typeof r.description === 'string' ? r.description.toLowerCase() : ''
      return needleTokens.every(t => words.some(w => w.startsWith(t)) || description.includes(t))
    }

    // Excel export — modeled after EquipmentTracker.exportToExcel. Search results are already
    // collapsed by the backend ($group on multiBox), so each row → one excel row (no outline).
    // Row coloring matches EquipmentTracker; the multiBox-consumed branch is dropped because
    // search rows don't carry availableCount and the per-sibling `quantity` would mislead.
    const equipmentExportRowRed = (row) => !!(
      row.shipmentId?.shipmentDate ||
      (row.rackId?.shipmentId?.shipmentState != null && row.rackId.shipmentId.shipmentState > 2) ||
      row.returnDisposition
    )
    const equipmentExportRowDimmed = (row) => !!(row.returnDisposition && ['transferred', 'disposed', 'damaged'].includes(row.returnDisposition))
    const equipmentExportRowInRackIndigo = (row) => {
      if (equipmentExportRowRed(row)) return false
      if (!row?.rackId?._id) return false
      if (row?.shipmentId?.shipmentState > 1) return false
      return true
    }
    const styleEquipmentExcelDataRow = (excelRow, columnCount, red, indigo, dimmed) => {
      const alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
      for (let c = 1; c <= columnCount; c++) {
        const cell = excelRow.getCell(c)
        if (red) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } }
          cell.font = { color: { argb: 'FF991B1B' } }
        } else if (indigo) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E7FF' } }
          cell.font = { color: { argb: 'FF3730A3' } }
        } else {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } }
          cell.font = dimmed ? { color: { argb: 'FF737373' } } : { color: { argb: 'FF171717' } }
        }
        cell.alignment = alignment
      }
    }
    const EXCEL_HEADERS = [
      { label: 'Assigned To',   key: 'customer',                  width: 32 },
      { label: 'Project',       key: 'project',                   width: 40 },
      { label: 'Box',           key: 'boxNumber',                 width: 10 },
      { label: 'Manufacturer',  key: 'manufacturer',              width: 28 },
      { label: 'Part #',        key: 'partNumber',                width: 24 },
      { label: 'Qty',           key: 'quantity',                  width: 8  },
      { label: 'Description',   key: 'description',               width: 48 },
      { label: 'Received',      key: 'createdAt',                 width: 14 },
      { label: 'Tracking #',    key: 'trackingNumber',            width: 24 },
      { label: 'Ship Date',     key: 'shipmentId.shipmentDate',   width: 22 },
      { label: 'State',         key: 'returnDisposition',         width: 16 },
      { label: 'Serial #',      key: 'serialNumber',              width: 28 },
      { label: 'MAC Address',   key: 'macAddress',                width: 24 },
      { label: 'Properties',    key: 'properties',                width: 36 },
      { label: 'Rack',          key: 'rackId.name',               width: 22 },
      { label: 'Room Number',     key: 'roomId.nameOne',          width: 22 },
      { label: 'Room Name',       key: 'roomId.nameTwo',          width: 22 },
      { label: 'Room Type',       key: 'roomId.roomType',         width: 22 },
      { label: 'Estimate Number', key: 'roomId.estimateNumber',   width: 12 }
    ]
    // Columns dropped from the fulfillment export — pool inventory has no assignment/shipment/placement,
    // so Ship Date / State / Rack / Room columns are noise there. Keyed to EXCEL_HEADERS.key.
    const FULFILLMENT_EXCLUDE_COLUMNS = new Set([
      'shipmentId.shipmentDate', 'returnDisposition', 'rackId.name', 'roomId.nameOne', 'roomId.nameTwo', 'roomId.roomType', 'roomId.estimateNumber'
    ])
    // Project label composition mirrors EquipmentSearchRow.projectLabel:
    //   prime:     companyProjectNumber · name
    //   non-prime: companyProjectNumber / clientProjectNumber · name
    const buildProjectLabel = (row) => {
      const p = row?.projectId
      if (!p?._id) return ''
      const companyNum = p.companyProjectNumber
      const clientNum = p.clientProjectNumber
      const name = p.name
      let label = companyNum || ''
      if (!isPrime.value && clientNum) label = label ? `${label} / ${clientNum}` : clientNum
      if (name) label = label ? `${label} · ${name}` : name
      return label
    }
    const getCellValue = (row, h) => {
      const key = h.key
      if (key === 'customer') return row.clientId?.name ?? ''
      if (key === 'project') return buildProjectLabel(row)
      if (key === 'manufacturer') {
        const raw = row.alias?.manufacturer ?? row.manufacturer ?? ''
        const lower = (raw != null && typeof raw === 'string') ? String(raw).trim().toLowerCase() : ''
        if (!lower) return raw
        return camelCaseToLabel(lower) ?? lower
      }
      if (key === 'partNumber')   return row.alias?.partNumber ?? row.partNumber ?? ''
      // Multi-piece units expose the sibling count via _multiBoxQty (added by the search $group);
      // single-box rows fall back to the raw quantity (defaulting to 1).
      if (key === 'quantity')     return row.multiBox ? (row._multiBoxQty ?? 1) : (row.quantity ?? 1)
      if (key === 'description')  return row.description ?? ''
      if (key === 'createdAt') {
        const v = row.createdAt
        return v && isValid(parseISO(v)) ? format(parseISO(v), 'MM/dd/yyyy') : ''
      }
      if (key === 'shipmentId.shipmentDate') {
        const v = row.shipmentId?.shipmentDate
        return v && isValid(parseISO(v)) ? format(parseISO(v), 'MM/dd/yyyy h:mm aaa') : ''
      }
      if (key === 'returnDisposition') return capitalizeString(row.returnDisposition ?? '') || ''
      if (key === 'serialNumber') {
        if (row.serialNumber) return String(row.serialNumber)
        if (Array.isArray(row.serialNumbers) && row.serialNumbers.length > 0) {
          return row.serialNumbers.map(s => s.serialNumber).filter(Boolean).join(', ')
        }
        return ''
      }
      if (key === 'macAddress') {
        if (row.macAddress) return String(row.macAddress)
        if (Array.isArray(row.macAddresses) && row.macAddresses.length > 0) {
          return row.macAddresses.map(m => m.macAddress).filter(Boolean).join(', ')
        }
        return ''
      }
      if (key === 'properties') {
        if (!Array.isArray(row.properties) || row.properties.length === 0) return ''
        return row.properties.map(p => (p.name && p.value != null) ? `${p.name}: ${p.value}` : '').filter(Boolean).join('; ')
      }
      if (key === 'trackingNumber') return row.trackingNumber ?? ''
      if (key === 'rackId.name')    return row.rackId?.name ?? ''
      if (key === 'roomId.nameOne') return row.roomId?.nameOne ?? ''
      if (key === 'roomId.nameTwo') return row.roomId?.nameTwo ?? ''
      if (key === 'roomId.roomType') return row.roomId?.roomType ?? ''
      if (key === 'roomId.estimateNumber') return row.roomId?.estimateNumber ?? ''
      if (key === 'boxNumber')      return formatBoxNumber(row.boxNumber, row.boxYear)
      return ''
    }
    const onExportExcel = async () => {
      // Snapshot so an in-flight reactive change can't reshape the export mid-build.
      const items = displayResults.value.slice()
      if (!items.length) return
      isExporting.value = true
      try {
        // Fulfillment export drops the assignment/shipment/placement columns.
        const headers = props.hideStateShipment ? EXCEL_HEADERS.filter(h => !FULFILLMENT_EXCLUDE_COLUMNS.has(h.key)) : EXCEL_HEADERS
        const workbook = new ExcelJs.Workbook()
        const worksheet = workbook.addWorksheet('Search Results')
        const headerRowExcel = worksheet.addRow(headers.map(h => h.label))
        headerRowExcel.eachCell((cell) => {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF444444' } }
          cell.font = { color: { argb: 'FFFFFFFF' }, bold: true }
          cell.alignment = { vertical: 'middle', horizontal: 'center' }
        })
        headers.forEach((h, i) => {
          worksheet.getColumn(i + 1).width = Math.max(8, Math.min(50, Number(h.width) || 12))
        })
        items.forEach((row) => {
          const excelRow = worksheet.addRow(headers.map(h => getCellValue(row, h)))
          styleEquipmentExcelDataRow(
            excelRow,
            headers.length,
            equipmentExportRowRed(row),
            equipmentExportRowInRackIndigo(row),
            equipmentExportRowDimmed(row)
          )
        })
        const dateStr = format(new Date(), 'yyyy-MM-dd')
        const fileName = `Equipment_Search_${dateStr}.xlsx`
        const buffer = await workbook.xlsx.writeBuffer()
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        a.target = '_blank'
        a.click()
        window.URL.revokeObjectURL(url)
      } catch (err) {
        console.error('Export to Excel failed:', err)
      } finally {
        isExporting.value = false
      }
    }

    // Auto-query only when there's real text to query; customer-alone auto-firing is deliberately excluded
    // so picking a customer doesn't trigger a heavy "everything for this customer" scan without user intent.
    // Routes through onSubmit so debounced auto-fire and explicit Enter/paste hit the same code path —
    // prevents inconsistent results between typing and explicit submit.
    const scheduleSearch = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        if (q.value.trim().length >= MIN_AUTO_QUERY_CHARS) onSubmit()
      }, DEBOUNCE_MS)
    }

  // Watchers
    // Lookback change: re-run search only when text is present in the input — same gating as the
    // customer chip. Toggling without text just changes the value silently and clears stale results
    // so they don't disagree with the new filter.
    watch(lookbackMonths, () => {
      if (q.value.trim().length >= MIN_AUTO_QUERY_CHARS) onSubmit()
      else resetResults()
    })

    // Exact-match toggle: re-run when there's text to refine; otherwise clear stale results so
    // the displayed set never disagrees with the new mode. Mirrors the lookback gating.
    watch(exactMatch, () => {
      if (q.value.trim().length >= MIN_AUTO_QUERY_CHARS) onSubmit()
      else resetResults()
    })

    // React to changes in the TRIMMED query — adding leading/trailing whitespace doesn't change
    // the trimmed value, so Vue's old/new compare skips the callback. Replaces the per-keystroke
    // onInput handler.
    watch(() => q.value.trim(), (trimmed) => {
      if (trimmed.length >= MIN_AUTO_QUERY_CHARS) {
        scheduleSearch()
      } else {
        if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null }
        cancelInFlight()
        if (!canQuery.value && results.value.length) {
          results.value = []
          hasRun.value = false
          resultMeta.value = { count: 0, approximate: false, hasMore: false, cappedAtLimit: false }
        }
      }
    })

  // Lifecycle
    onMounted(() => {
      window.addEventListener('data_change_from_socket', onEquipmentChange)
      window.addEventListener('dayChanged', onDayChanged)
      nextTick(() => searchInputRef.value?.focus())
    })
    onBeforeUnmount(() => {
      window.removeEventListener('data_change_from_socket', onEquipmentChange)
      window.removeEventListener('dayChanged', onDayChanged)
      if (debounceTimer) clearTimeout(debounceTimer)
      cancelInFlight()
    })

  // Handlers
    // Bumps todayTick so child rows' time-sensitive computeds re-run. Driven by the global
    // 'dayChanged' window event fired from src/components/utils/dayChangeNotifier.js.
    const onDayChanged = () => { todayTick.value++ }

    // Toggle the lookback between the default window and "All time". The watcher above re-runs
    // the search if there's an active query; otherwise it just changes the value silently.
    const toggleLookback = () => {
      lookbackMonths.value = lookbackMonths.value ? 0 : DEFAULT_LOOKBACK_MONTHS
    }

    const toggleExactMatch = () => {
      exactMatch.value = !exactMatch.value
    }

    const onSubmit = () => {
      if (!canQuery.value) return
      // Any explicit submit supersedes a pending debounce.
      if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null }
      runSearch()
    }

    // Paste: let v-model populate, then fire immediately (skip debounce). The trim watcher would
    // also fire after debounce, but pastes are clear intent — kick off now and the watcher's
    // pending schedule gets cleared via onSubmit's clearTimeout.
    const onPaste = () => { setTimeout(onSubmit, 0) }

    // Customer chip changes only fire a search when there's already text to refine with.
    // Without text, we still clear results so they never disagree with the current chip state —
    // the user then clicks Search or types to get results under the new filter.
    const resetResults = () => {
      results.value = []
      hasRun.value = false
      resultMeta.value = { count: 0, approximate: false, hasMore: false, cappedAtLimit: false }
      if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null }
      cancelInFlight()
    }
    const selectClient = (item) => {
      filters.value = { client: item || {} }
      if (q.value.trim().length >= MIN_AUTO_QUERY_CHARS) onSubmit()
      else resetResults()
    }
    const clearClient = () => {
      filters.value = { client: {} }
      if (q.value.trim().length >= MIN_AUTO_QUERY_CHARS) onSubmit()
      else resetResults()
    }

    const clearAll = () => {
      q.value = ''
      results.value = []
      hasRun.value = false
      localFilters.value = { state: '', shipment: '', query: '' }
      lookbackMonths.value = DEFAULT_LOOKBACK_MONTHS
      exactMatch.value = false
      resultMeta.value = { count: 0, approximate: false, hasMore: false, cappedAtLimit: false }
      if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null }
      cancelInFlight()
    }

    const openDetail = (id) => { detailId.value = id }
    const toggleRowMenu = (row) => {
      openMenuId.value = openMenuId.value === row._id ? null : row._id
    }
    const onDetailUpdated = () => {
      if (detailId.value) fetchAndReplaceOne(detailId.value)
    }

    // Row owns the legacy fetch + per-row UX feedback. Parent only opens the Modal when the
    // row hands up a mongo packingListId via the openPackingList event payload.
    const onOpenPackingList = (equipment) => {
      const plId = equipment?.packingListId
      if (!plId) return
      packingListDetailId.value = typeof plId === 'object' ? String(plId._id || plId) : String(plId)
    }

    // Socket-driven live-list update. An equipment-tagged event carries one or more equipment ids;
    // for each id that matches a current non-multiBox row, silently refresh just that row.
    // MultiBox rows are excluded — the collapsed row's $first-sibling state can't be kept accurate
    // from a single-sibling event, and we deliberately hide state chips for them anyway.
    const onEquipmentChange = (e) => {
      if (e?.detail?.type !== 'equipment') return
      const data = e.detail.data || {}
      const ids = [data.equipmentId, ...(Array.isArray(data.equipmentIds) ? data.equipmentIds : [])]
        .filter(id => typeof id === 'string' && id.length > 0)
      if (!ids.length || !results.value.length) return
      for (const id of ids) {
        const row = results.value.find(r => r._id === id)
        if (!row || row.multiBox) continue
        fetchAndReplaceOne(id)
      }
    }

    return {
      authenticated,
      q, results, filters, localFilters, lookbackMonths, exactMatch,
      isWorking, isExporting, responseError, hasRun,
      detailId, packingListDetailId, searchInputRef, openMenuId, todayTick,
      isPrime, canDelete, canQuery, inputBorderClass, displayResults,
      countLabel, canLoadMore, canLoadAll, showNarrowHint, hasActiveLocalFilters,
      DEFAULT_LOOKBACK_MONTHS,
      toggleLookback, toggleExactMatch,
      onSubmit, onPaste,
      selectClient, clearClient,
      loadMore, loadAll,
      clearAll, openDetail, onDetailUpdated, toggleRowMenu, onOpenPackingList, deleteBox,
      onExportExcel
    }
  }
}
</script>

<style scoped>
/* Hide WebKit's native type="search" clear button so the custom X is the only clear control.
   The custom one does a full reset (results, local filters, pending debounce) — native only empties the input. */
input[type="search"]::-webkit-search-cancel-button {
  -webkit-appearance: none;
  display: none;
}
</style>
